import { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Sparkles } from "lucide-react";
import "@/App.css";

import { Navigation } from "./components/Navigation";
import { HeroSection } from "./components/HeroSection";
import { PulseCard } from "./components/PulseCard";
import { LearningCard } from "./components/LearningCard";
import { OrgPulseCard } from "./components/OrgPulseCard";
import { AMACard } from "./components/AMACard";
import { VideoCard } from "./components/VideoCard";
import { CoachPanel } from "./components/CoachPanel";
import { RefreshIndicator } from "./components/RefreshIndicator";
import { DetailModal, AMAManageModal, LearningGalleryModal } from "./components/Modals";
import { SearchCommand } from "./components/SearchCommand";
import { NotificationsSheet } from "./components/NotificationsSheet";
import { ExternalLinkDialog } from "./components/ExternalLinkDialog";
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [userProfile, setUserProfile] = useState("user");
  const [aiLevel, setAiLevel] = useState("intermediate");
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [coachOpen, setCoachOpen] = useState(false);
  const [coachQuery, setCoachQuery] = useState("");
  const [refreshStatus, setRefreshStatus] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [detailModal, setDetailModal] = useState({ open: false, type: null, data: null });
  const [amaManageOpen, setAmaManageOpen] = useState(false);
  const [learningGalleryOpen, setLearningGalleryOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [allPathways, setAllPathways] = useState([]);
  const [amaQuestions, setAmaQuestions] = useState([]);
  const [externalLink, setExternalLink] = useState({ open: false, url: "", title: "", sourceName: "" });

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/dashboard`, {
        params: { user_profile: userProfile, ai_level: aiLevel }
      });
      setDashboardData(response.data);
      setRefreshStatus(response.data.refresh_state);
      setError(null);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      setError("Failed to load dashboard. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [userProfile, aiLevel]);

  const fetchAdditionalData = useCallback(async () => {
    try {
      const [pathwaysRes, amaRes] = await Promise.all([
        axios.get(`${API}/learning/pathways`, { params: { user_profile: userProfile } }),
        axios.get(`${API}/ama`, { params: { user_profile: userProfile } })
      ]);
      setAllPathways(pathwaysRes.data.pathways || []);
      setAmaQuestions(amaRes.data.questions || []);
    } catch (err) {
      console.error("Additional data fetch error:", err);
    }
  }, [userProfile]);

  useEffect(() => {
    fetchDashboard();
    fetchAdditionalData();
  }, [fetchDashboard, fetchAdditionalData]);

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      const response = await axios.post(`${API}/refresh`);
      const jobId = response.data.job_id;
      const pollStatus = async () => {
        try {
          const statusRes = await axios.get(`${API}/refresh/status`, { params: { job_id: jobId } });
          setRefreshStatus(statusRes.data);
          if (statusRes.data.status === "completed") {
            setIsRefreshing(false);
            await fetchDashboard();
            await fetchAdditionalData();
          } else if (statusRes.data.status === "failed") {
            setIsRefreshing(false);
            setError("Refresh failed. Using cached data.");
          } else {
            setTimeout(pollStatus, 1000);
          }
        } catch { setIsRefreshing(false); }
      };
      pollStatus();
    } catch {
      setIsRefreshing(false);
      setError("Failed to start refresh.");
    }
  };

  const handleQuickCoach = (query) => { setCoachQuery(query); setCoachOpen(true); };
  const handleNavClick = (section) => {
    if (section === "coach") setCoachOpen(true);
    else if (section === "learning") setLearningGalleryOpen(true);
  };

  const handleItemClick = (type, data) => {
    const url = data?.source_url || data?.video_url || data?.metadata?.source?.url;
    if (url && url.startsWith("http")) {
      setExternalLink({
        open: true, url,
        title: data.title || data.headline || "",
        sourceName: data.channel_name || data.platform || data.metadata?.source?.name || "",
      });
    } else {
      setDetailModal({ open: true, type, data });
    }
  };

  const handleSearchResult = (type, data) => handleItemClick(type, data);
  const handleAmaSummarize = async (clusterId) => {
    const response = await axios.post(`${API}/ama/summarize`, { cluster_id: clusterId });
    return response.data;
  };
  const handleAmaRespond = async (clusterId, responseText) => {
    await axios.post(`${API}/ama/respond`, { cluster_id: clusterId, response_text: responseText });
    await fetchDashboard();
  };
  const handleAmaPin = async (responseId, pinned) => {
    await axios.post(`${API}/ama/pin`, { response_id: responseId, pinned });
    await fetchDashboard();
  };

  const getModule = (moduleId) => dashboardData?.modules?.find(m => m.module_id === moduleId);

  if (loading && !dashboardData) {
    return (
      <div className="min-h-screen bg-[#050B14]" data-testid="loading-screen">
        <Navigation
          userProfile={userProfile} aiLevel={aiLevel}
          onProfileChange={setUserProfile} onAiLevelChange={setAiLevel}
          onNavClick={handleNavClick} onSearchOpen={() => setSearchOpen(true)}
          onNotificationsOpen={() => setNotificationsOpen(true)}
        />
        <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
            <div className="md:col-span-12 h-48 rounded-xl skeleton" />
            <div className="md:col-span-4 h-64 rounded-xl skeleton" />
            <div className="md:col-span-8 h-64 rounded-xl skeleton" />
            <div className="md:col-span-7 h-48 rounded-xl skeleton" />
            <div className="md:col-span-5 h-48 rounded-xl skeleton" />
          </div>
        </main>
      </div>
    );
  }

  const pulseModule = getModule("card.pulse");
  const learningModule = getModule("card.learning");
  const orgPulseModule = getModule("card.orgPulse");
  const videoModule = getModule("card.video");
  const amaModule = getModule("card.ama");
  const theme = dashboardData?.profile?.theme;

  return (
    <div className="min-h-screen bg-[#050B14] overflow-x-hidden" data-testid="dashboard">
      <Navigation
        userProfile={userProfile} aiLevel={aiLevel}
        onProfileChange={setUserProfile} onAiLevelChange={setAiLevel}
        onRefresh={handleRefresh} isRefreshing={isRefreshing}
        onNavClick={handleNavClick} onSearchOpen={() => setSearchOpen(true)}
        onNotificationsOpen={() => setNotificationsOpen(true)}
      />

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 pb-24 md:pb-8">
        {error && (
          <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm" data-testid="error-banner">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {/* Hero - full width */}
          <div className="md:col-span-12 stagger-in">
            <HeroSection
              theme={theme} userProfile={userProfile} aiLevel={aiLevel}
              onAskCoach={() => setCoachOpen(true)}
              onExploreTheme={() => setLearningGalleryOpen(true)}
            />
          </div>

          {/* Today's Pulse */}
          <div className="md:col-span-4 stagger-in">
            <PulseCard
              items={pulseModule?.items || []} userProfile={userProfile}
              onItemClick={(item) => handleItemClick("pulse", item)}
            />
          </div>

          {/* Learning */}
          <div className="md:col-span-8 stagger-in">
            <LearningCard
              pathways={learningModule?.pathways || []} aiLevel={aiLevel}
              onItemClick={(item) => handleItemClick("learning", item)}
              onBrowseAll={() => setLearningGalleryOpen(true)}
            />
          </div>

          {/* Org Pulse */}
          <div className="md:col-span-7 stagger-in">
            <OrgPulseCard
              items={orgPulseModule?.items || []} userProfile={userProfile}
              onItemClick={(item) => handleItemClick("orgPulse", item)}
            />
          </div>

          {/* AMA */}
          <div className="md:col-span-5 stagger-in">
            <AMACard
              clusters={amaModule?.clusters || []} userProfile={userProfile}
              onManage={() => setAmaManageOpen(true)}
            />
          </div>

          {/* Videos */}
          <div className="md:col-span-12 stagger-in">
            <VideoCard
              videos={videoModule?.items || []} userProfile={userProfile}
              onItemClick={(item) => handleItemClick("video", item)}
            />
          </div>
        </div>
      </main>

      {/* Floating AI Coach Button - only on desktop */}
      {!coachOpen && (
        <button
          onClick={() => setCoachOpen(true)}
          className="hidden md:flex fixed bottom-6 right-6 z-40 items-center gap-2 px-5 py-3 rounded-full bg-[#14B8A6] text-[#050B14] font-semibold shadow-lg shadow-[#14B8A6]/20 hover:shadow-[#14B8A6]/30 hover:scale-105 transition-all"
          data-testid="coach-fab"
        >
          <Sparkles className="w-5 h-5" />
          AI Coach
        </button>
      )}

      {refreshStatus && <RefreshIndicator status={refreshStatus} isRefreshing={isRefreshing} />}
      <CoachPanel isOpen={coachOpen} onClose={() => setCoachOpen(false)} initialQuery={coachQuery} userProfile={userProfile} aiLevel={aiLevel} />
      <SearchCommand open={searchOpen} onOpenChange={setSearchOpen} onSelectResult={handleSearchResult} />
      <NotificationsSheet open={notificationsOpen} onOpenChange={setNotificationsOpen} />
      <ExternalLinkDialog
        open={externalLink.open}
        onClose={() => setExternalLink({ open: false, url: "", title: "", sourceName: "" })}
        url={externalLink.url} title={externalLink.title} sourceName={externalLink.sourceName}
      />
      <DetailModal
        isOpen={detailModal.open}
        onClose={() => setDetailModal({ open: false, type: null, data: null })}
        type={detailModal.type} data={detailModal.data} userProfile={userProfile}
        onExternalLink={(url, title, sourceName) => setExternalLink({ open: true, url, title, sourceName })}
      />
      <AMAManageModal
        isOpen={amaManageOpen} onClose={() => setAmaManageOpen(false)}
        clusters={amaModule?.clusters || []} questions={amaQuestions}
        onSummarize={handleAmaSummarize} onRespond={handleAmaRespond} onPin={handleAmaPin}
      />
      <LearningGalleryModal
        isOpen={learningGalleryOpen} onClose={() => setLearningGalleryOpen(false)}
        pathways={allPathways}
        onSelectPathway={(pathway) => { setLearningGalleryOpen(false); handleItemClick("learning", pathway); }}
      />

      {/* Mobile Bottom Nav */}
      <nav className="fixed bottom-0 left-0 w-full z-40 flex justify-center md:hidden border-t border-white/[0.06]"
        style={{ background: "rgba(10, 25, 47, 0.90)", backdropFilter: "blur(20px)" }}
        data-testid="mobile-nav"
      >
        <div className="flex items-center justify-around w-full max-w-md py-2 px-4">
          <button className="flex flex-col items-center p-2 text-[#14B8A6]" data-testid="nav-dashboard">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M4 13h6a1 1 0 001-1V4a1 1 0 00-1-1H4a1 1 0 00-1 1v8a1 1 0 001 1zm-1 7a1 1 0 001 1h6a1 1 0 001-1v-4a1 1 0 00-1-1H4a1 1 0 00-1 1v4zm10 0a1 1 0 001 1h6a1 1 0 001-1v-7a1 1 0 00-1-1h-6a1 1 0 00-1 1v7zm1-10h6a1 1 0 001-1V4a1 1 0 00-1-1h-6a1 1 0 00-1 1v5a1 1 0 001 1z"/></svg>
            <span className="text-xs mt-1">Dashboard</span>
          </button>
          <button onClick={() => setCoachOpen(true)} className="flex flex-col items-center p-2 text-[#94A3B8] hover:text-[#14B8A6]" data-testid="nav-coach">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
            <span className="text-xs mt-1">AI Coach</span>
          </button>
          <button onClick={() => setLearningGalleryOpen(true)} className="flex flex-col items-center p-2 text-[#94A3B8] hover:text-[#14B8A6]" data-testid="nav-learn">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
            <span className="text-xs mt-1">Learn</span>
          </button>
        </div>
      </nav>

      <Toaster position="bottom-right" />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="*" element={<Dashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
